import { useContext, useEffect, useState } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { SocketIOContext } from "../contexts/SocketIOContext";
import useProfileData from "../hooks/UserProfileData";
import { useAppDispatch, useAppSelector } from "../hooks/storeHook";
import { NotificationObject } from "../interfaces";

import { showNotifications, updateShowNotifications } from "../store/reducers/mainReducer";
import NetworkInstance1 from "../utils/alternate";
import BaseText from "./BaseText";
import SvgComponent from "./SVGShape";
import NotificationItem from "./notifications/NotificationItem";
import UseDynamicLogo from "../hooks/UseDynamicLogo";
import UserImgComponent from "./UserImgComponent";
import mixin from "../utils/helperFunction";

interface NavbarProps {
  onToggleSidebar?: () => void;
  isSidebarVisible: boolean;
  empty?: boolean
}

const NavigationBar: React.FC<NavbarProps> = ({
  onToggleSidebar,
  empty=false,
  isSidebarVisible,
}) => {
  const notificationSound = new Audio('/tones/tone-2870.wav');

  const notificationsModal = useAppSelector(showNotifications);
  const dispatch = useAppDispatch();
  let navigate: NavigateFunction = useNavigate();

  const [allNotifications, setAllNotifications] = useState<NotificationObject[]>([])

  const { firstName='', lastName='', jobTitle='', profileImageUrl='', user } = useProfileData();
  const { notificationsSocket, isNotificationsConnected } = useContext(SocketIOContext);
  const networkInstance = NetworkInstance1();



  const computedName = () => `${firstName || ""} ${lastName || ""}`;
  const computedRole = () => (jobTitle ? `${jobTitle.name}` : "");
  const computedProfileURL = () => profileImageUrl;
    
    const handleClick = () => {
      dispatch(updateShowNotifications(!notificationsModal))
    }

  const logo = UseDynamicLogo();

    const deleteNotification = async (id:string) => {
      try {
        await networkInstance.delete(`/notifications/${id}`);
      } catch (err: any) {
        const { data = {} } = err?.response;
      } finally {
      }
    }

    const setNotificationToRead = async (notificationId:string) => {
      try {
        await networkInstance.patch(`/notifications/mark-as-read/${notificationId}`);
      } catch (err: any) {
        console.log({ err })
      }
    } 

    const handleDeleteNotification = async (id:string) => {
      const updatedArray = allNotifications.filter(notification => notification.id !== id);
      setAllNotifications(updatedArray);
      deleteNotification(id)
    }

    const handleReadNotification = async (id:string) => {
      setAllNotifications((prevRecords) => {
        return prevRecords.map(notification => {
          if (notification.id === id) {
            return { ...notification, isRead: true };
          } else {
            return notification;
          }
        });
      });      
      setNotificationToRead(id)
    }

    useEffect(() => {
      if (user && user?.id) {
        if (isNotificationsConnected) {
          notificationsSocket.emit('joinNotificationChannel', user?.id);
        }
      }
    }, [isNotificationsConnected, notificationsSocket, user, user?.id]);


    useEffect(() => {
      if (user?.id) {
        const fetchAllNotifications = async () => {
          try {
            const { data, status } = await networkInstance.get(`/notifications/get-by-user/${user.id}`);
            if (status === 200) {
              setAllNotifications(data);
            }
          } catch (err: any) {
            const { data = {} } = err?.response;
          } finally {
          }
        };
       fetchAllNotifications()
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id])

    useEffect(() => {
      if (isNotificationsConnected) {
        notificationsSocket.on('joinedNotificationChannel', (data) => {
          console.log('we in business:', data); 
        });

        const handleNewNotification = (dataNotification:NotificationObject) => {
          notificationSound.play().catch(error => {
            console.error('Failed to play audio:', error);
          });
        setAllNotifications((prevRecords) => [...prevRecords, dataNotification]);
        }
      
        notificationsSocket.on('newNotification', (dataNotification:any) => handleNewNotification(dataNotification));

      }
      // Function to clean up event listeners when the component unmounts
      const cleanup = () => {
        notificationsSocket.off('joinedNotificationChannel');
        notificationsSocket.off('newNotification');
      };
    
      // Add the cleanup function to the effect's dependency array
      return cleanup;
    }, [isNotificationsConnected, notificationsSocket]);

    const unreadNotifications = allNotifications && allNotifications.filter(notification => notification.isRead === false)

  return (
    <div className="bg-white border border-bottom-gray-100 ">
      <div className={`flex justify-between items-center ${empty ? ' py-4 px-6 lg:px-6' : 'py-2 px-4'}  `}>
        <img width={150} height="auto" src={logo} alt="Talenvo App" onClick={() => navigate('/dashboard')} />
        {!empty && <div className="items-center relative hidden lg:flex">
          {/* Notification */}
          <div
            className="mr-20 md:mr-5 bg-white relative p-4 px-5 rounded-xl cursor-pointer"
            onClick={() => handleClick()}
          >
            <SvgComponent icon="blue-bell" />
            {unreadNotifications?.length > 0 && <div className="bg-[#D72830] rounded-full absolute w-4 text-center top-2 right-3 h-4">
              <BaseText size="xs-small" color="white">
                { unreadNotifications?.length || 0 }
              </BaseText>
            </div>}
          </div>
          {notificationsModal && (
            <div
              className="h-screen overflow-scroll absolute
          top-10 right-0 p-4 scrollbar-hide z-30"
            >
              <NotificationItem notifications={allNotifications} setNotificationToRead={(notificationId) => handleReadNotification(notificationId)} deleteNotification={(notificationId) => handleDeleteNotification(notificationId) } />
            </div>
          )}
          {/* Notification ends here */}

          <div className="relative cursor-pointer" onClick={() => {
            if (user?.profile?.id)
              navigate(`/profile/${user?.profile?.id || ''}`);
       }} >
          <UserImgComponent computedProfileURL={computedProfileURL()} altText="User's Profile Picture" initials={mixin.initials(firstName, lastName)} />
            <div className={` ${isNotificationsConnected ? 'bg-green-400' : 'bg-gray-400' } ${isNotificationsConnected ? 'text-green-400' : 'text-gray-400' } ${ computedProfileURL() ? ' bottom-1 right-0' : 'bottom-0 -right-1' } absolute w-2 h-2 rounded-full `}></div>
          </div>

          <div className="flex flex-col ml-4 mr-8">
            <BaseText weight="medium" size="large">
              {computedName()}
            </BaseText>
            <BaseText color="gray">{computedRole()}</BaseText>
          </div>
        </div>}
       {!empty && <div className="items-center relative lg:hidden flex ease-in-out transition-all gap-2 duration-300">
        <button
            onClick={() => handleClick()}
            className="outline-0 focus:outline-none bg-white relative p-4 rounded-xl cursor-pointer"
          >
            <SvgComponent icon="blue-bell" />
            <div className="bg-[#D72830] rounded-full absolute  text-center top-1 right-2 min-w-4 p-[2px] ">
              <BaseText size="xs-small" color="white">
              { allNotifications?.length || 0 }
              
              </BaseText>
            </div>
          </button>
          {notificationsModal && (
            <div
              className="h-screen w-96 overflow-scroll absolute
          top-10 right-0 p-4 scrollbar-hide z-30"
            >
              <NotificationItem notifications={allNotifications} setNotificationToRead={(notificationId) => handleReadNotification(notificationId)} deleteNotification={(notificationId) => handleDeleteNotification(notificationId)} />
            </div>
          )}
          <button
            onClick={() => onToggleSidebar && onToggleSidebar()}
            className="outline-0 focus:outline-none bg-[#CBD4E9] relative p-2 rounded-xl cursor-pointer"
          >
            {isSidebarVisible ? (
              <SvgComponent cancelIconLarger icon="cancel" />
            ) : (
              <SvgComponent icon="hamburger-icon" />
            )}
          </button>
        </div>}
      </div>
    </div>
  );
};

export default NavigationBar;
